import TempoModal from "@/components/Core/TempoModal";
import Spinner from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { useActiveProject } from "@/utils/utils";
import { useState } from "react";
import { toast } from "sonner";
import { useCustomKnowledge } from "../../hooks/useCustomKnowledge";
import { EllipsisIcon, RefreshCcw } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function CustomKnowledge() {
  const [showResetPromptModal, setShowResetPromptModal] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const project = useActiveProject();

  const {
    currentPrompt,
    setCurrentPrompt,
    hasUnsavedChanges,
    savePrompt,
    resetPrompt,
    cancelChanges,
  } = useCustomKnowledge(project);

  const handleSave = async () => {
    if (!project) {
      toast.error("Project not found");
      return;
    }

    try {
      const success = await savePrompt();
      if (!success) {
        throw new Error("Failed to save custom knowledge");
      }
      toast.success("Custom knowledge saved successfully");
    } catch (error) {
      toast.error(`Error saving custom knowledge: ${error.message}`);
    }
  };

  const handleResetClick = () => {
    setShowResetPromptModal(true);
    setIsPopoverOpen(false);
  };

  const handleReset = async () => {
    setShowResetPromptModal(false);
    if (!project) {
      toast.error("Project not found");
      return;
    }

    try {
      const success = await resetPrompt();
      if (!success) {
        throw new Error("Failed to reset custom knowledge");
      }
      toast.success("Custom knowledge reset successfully");
    } catch (error) {
      toast.error(`Error resetting custom knowledge: ${error.message}`);
    }
  };

  return (
    <div className="flex flex-col rounded-md">
      {showResetPromptModal && (
        <Dialog
          open={showResetPromptModal}
          onOpenChange={setShowResetPromptModal}
        >
          <DialogContent className="border border-secondary">
            <DialogHeader>
              <DialogTitle className="-mx-6 px-6 pb-4 border-b font-medium border-secondary">
                Reset to Default
              </DialogTitle>
              <DialogDescription className="pt-2 text-gray-200">
                Are you sure you want to reset to default? You'll permanently
                lose all of your changes, and this cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {/* The footer with full-width border */}
            <DialogFooter className="-mx-6 px-6 pt-[16px] border-t border-secondary">
              <Button
                variant="secondary"
                className="rounded-lg"
                onClick={() => setShowResetPromptModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReset}
                variant="primary"
                className="rounded-lg"
              >
                Reset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <div className="flex flex-row gap-2 px-2 py-3.5 justify-between items-center border-b border-secondary">
        <p className="text-sm font-medium">Custom Knowledge</p>
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger>
            <div className="hover:bg-[#1a1b1d] rounded-lg flex justify-center items-center p-2">
              <EllipsisIcon className="w-4 h-4 hover:cursor-pointer" />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="w-[200px] hover:cursor-pointer hover:bg-[#1a1b1d] rounded-lg flex justify-start items-center gap-2 p-[14px] text-sm"
            side="bottom"
            align="start"
            alignOffset={5}
            sideOffset={5}
            onClick={handleResetClick}
          >
            <RefreshCcw className="w-4 h-4 text-gray-500" />
            Reset to default
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex flex-col border-b border-secondary w-full p-2 gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-normal">Customize Tempo's system prompt</p>
          <p className="text-xs font-normal text-secondary-n-400">
            Add specific instructions, context or documentation for the custom
            Tempo's AI to follow.
          </p>
        </div>
        <Textarea
          value={currentPrompt}
          onChange={(e) => setCurrentPrompt(e.target.value)}
          placeholder="Use #131519 for dark mode"
          className="w-full min-h-[96px] text-[12px] p-2 resize-none bg-[#1a1b1d] text-white rounded-md placeholder:text-secondary-600 rounded-lg"
        />
        {hasUnsavedChanges && (
          <div className="flex flex-row gap-2">
            <Button
              onClick={handleSave}
              variant="primary"
              className="rounded-lg w-full"
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
